import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";
import IQBOCustomFieldDefinition from "@models/QuickBooks Online/IQBOCustomFieldDefinition";
import IQBOCustomFieldMapping from "@models/QuickBooks Online/IQBOCustomFieldMapping";
import { IQBOMappableField } from "@models/QuickBooks Online/QBOMappableFields";
import OwButton from "../../../../shared/OwButton/OwButton";
import OwInputWrapper from "../../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import OwRadioSelect from "../../../../shared/OwRadioSelect/OwRadioSelect";
import WhatsThis from "../../../../shared/WhatsThis/WhatsThis";
import "./CustomFieldMappings.scss";

type Props = {
    /** Custom field definitions already filtered to invoice-applicable targets by the parent. */
    definitions: IQBOCustomFieldDefinition[];
    /** Mappable OrderWerks fields (sources). */
    mappableFields: IQBOMappableField[];
    /** Mappings currently saved on the supplier (pre-populate the editor). */
    existingMappings: IQBOCustomFieldMapping[];
    /** Enhanced custom fields gate. When false the editor is read-only. */
    isEnhanced: boolean;
    /**
     * Definitions already used by the PO#/SalesRep/RepGroup settings. Shown in the target dropdown but
     * disabled, with a note pointing at the setting that owns them (the backend treats those as the winner).
     * `usedByLabel` is a translation key (e.g. "po_invoice_custom_field").
     */
    reservedDefinitions: { id: string; usedByLabel: string }[];
    saving: boolean;
    onSave: (mappings: IQBOCustomFieldMapping[]) => void;
};

type SourceType = "field" | "static" | "order";

type MappingRow = {
    rowId: string;
    definitionId: string; // legacyIDV2 of the chosen definition ("" = none chosen)
    sourceType: SourceType;
    sourceField: string; // mappable field path (sourceType === "field")
    staticValue: string; // static value (sourceType === "static")
    defaultValue: string; // fallback for "field"/"order" rows
};

const STRING_LIST = "STRING_LIST";

// Source-field groups to hide from the "Order field" dropdown (e.g. the per-industry "Order Detail" set).
const EXCLUDED_SOURCE_GROUPS = new Set<string>(["order_detail"]);

const toSourceType = (sourceType?: string): SourceType => (sourceType === "static" || sourceType === "order" ? sourceType : "field");

const rowsFromMappings = (mappings: IQBOCustomFieldMapping[]): MappingRow[] =>
    (mappings ?? []).map((mapping) => ({
        rowId: v4(),
        definitionId: mapping.definitionId ?? "",
        sourceType: toSourceType(mapping.sourceType),
        sourceField: mapping.sourceField ?? "",
        staticValue: mapping.sourceType === "static" ? (mapping.defaultValue ?? "") : "",
        // For "field"/"order" rows defaultValue is the fallback; "static" uses staticValue instead.
        defaultValue: mapping.sourceType === "static" ? "" : (mapping.defaultValue ?? ""),
    }));

const emptyRow = (): MappingRow => ({
    rowId: v4(),
    definitionId: "",
    sourceType: "field",
    sourceField: "",
    staticValue: "",
    defaultValue: "",
});

const CustomFieldMappings = (props: Props) => {
    const intl = useIntl();
    const t = (id: string, defaultMessage?: string) => intl.formatMessage({ id, defaultMessage: defaultMessage ?? id });

    const [rows, setRows] = useState<MappingRow[]>(() => rowsFromMappings(props.existingMappings));

    // Re-seed rows when the saved mappings change (e.g. after a save round-trips a fresh supplierModel).
    useEffect(() => {
        setRows(rowsFromMappings(props.existingMappings));
    }, [props.existingMappings]);

    const findDefinition = (definitionId: string) => props.definitions.find((definition) => definition.legacyIDV2 === definitionId);

    // Saved mappings cache their own label/dataType, so a target can still be resolved when its definition
    // isn't in the currently-loaded set (definitions still loading, or no longer invoice-applicable). Used to
    // avoid silently dropping — and thereby deleting — previously saved mappings on save.
    const persistedByDefinitionId = useMemo(
        () => new Map((props.existingMappings ?? []).map((mapping) => [mapping.definitionId, mapping])),
        [props.existingMappings]
    );

    // Source fields grouped into <optgroup> sections by `group`, preserving first-seen group order.
    const groupedFields = useMemo(() => {
        const groups: { group: string; fields: IQBOMappableField[] }[] = [];
        for (const field of props.mappableFields ?? []) {
            if (EXCLUDED_SOURCE_GROUPS.has(field.group)) {
                continue;
            }
            let bucket = groups.find((entry) => entry.group === field.group);
            if (!bucket) {
                bucket = { group: field.group, fields: [] };
                groups.push(bucket);
            }
            bucket.fields.push(field);
        }
        return groups;
    }, [props.mappableFields]);

    const updateRow = (rowId: string, changes: Partial<MappingRow>) => {
        setRows((current) => current.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)));
    };

    const addRow = () => setRows((current) => [...current, emptyRow()]);

    const removeRow = (rowId: string) => setRows((current) => current.filter((row) => row.rowId !== rowId));

    const handleTargetChange = (rowId: string, definitionId: string) => {
        const definition = findDefinition(definitionId);
        // STRING_LIST fields have no natural order source, so default them to "Capture per order". Also clear
        // any stale STRING_LIST static value when the target changes.
        const stringListDefault = definition?.dataType === STRING_LIST ? { sourceType: "order" as SourceType } : {};
        updateRow(rowId, { definitionId, staticValue: "", ...stringListDefault });
    };

    // True when the given value is valid for the definition (STRING_LIST values must be a known option).
    // A missing definition (not currently loaded) can't be re-validated; accept it since it was valid when saved.
    const isValidValue = (definition: IQBOCustomFieldDefinition | undefined, value: string): boolean => {
        if (!value || !definition || definition.dataType !== STRING_LIST) {
            return true;
        }
        return (definition.dropDownOptions ?? []).some((option) => option.value === value);
    };

    // Build the payload + run the same validations the backend enforces. Returns null on a blocking error.
    const buildMappings = (): IQBOCustomFieldMapping[] | null => {
        const mappings: IQBOCustomFieldMapping[] = [];
        const seen = new Set<string>();

        for (const row of rows) {
            if (!row.definitionId) {
                continue; // skip incomplete rows (no target chosen)
            }

            const definition = findDefinition(row.definitionId);
            const persisted = persistedByDefinitionId.get(row.definitionId);

            // Don't silently drop a chosen target we can't resolve (that would delete a saved mapping). If the
            // definition isn't loaded but the mapping was previously saved, resolve from its cached metadata;
            // only block the save when it can't be resolved at all (e.g. definitions still loading).
            if (!definition && !persisted) {
                window.alert(
                    t(
                        "error_custom_field_mapping_unavailable_target",
                        "A mapped QuickBooks field is still loading or unavailable. Please wait for it to load and try again."
                    )
                );
                return null;
            }

            if (seen.has(row.definitionId)) {
                window.alert(t("error_duplicate_custom_field_mapping_target", "Each custom field can only be mapped once."));
                return null;
            }
            seen.add(row.definitionId);

            const base = {
                definitionId: row.definitionId,
                label: definition?.label ?? persisted?.label ?? "",
                // The definition's dataType is loosely typed (string); narrow to the mapping's union. Fall back
                // to the saved mapping's cached dataType, then "STRING" as a safe non-empty default.
                dataType: (definition?.dataType ?? persisted?.dataType ?? "STRING") as IQBOCustomFieldMapping["dataType"],
            };

            if (row.sourceType === "field") {
                if (!row.sourceField) {
                    window.alert(t("error_custom_field_mapping_missing_source", "Choose an order field for each mapping."));
                    return null;
                }
                if (!isValidValue(definition, row.defaultValue)) {
                    window.alert(t("error_custom_field_mapping_invalid_option", "Choose a valid option for the value."));
                    return null;
                }
                mappings.push({ ...base, sourceType: "field", sourceField: row.sourceField, defaultValue: row.defaultValue || undefined });
            } else if (row.sourceType === "order") {
                // Captured per order — no sourceField. defaultValue is an optional fallback.
                if (!isValidValue(definition, row.defaultValue)) {
                    window.alert(t("error_custom_field_mapping_invalid_option", "Choose a valid option for the value."));
                    return null;
                }
                mappings.push({ ...base, sourceType: "order", defaultValue: row.defaultValue || undefined });
            } else {
                if (!isValidValue(definition, row.staticValue)) {
                    window.alert(t("error_custom_field_mapping_invalid_option", "Choose a valid option for the value."));
                    return null;
                }
                mappings.push({ ...base, sourceType: "static", defaultValue: row.staticValue || undefined });
            }
        }

        return mappings;
    };

    const handleSave = () => {
        const mappings = buildMappings();
        if (mappings === null) {
            return;
        }
        props.onSave(mappings);
    };

    const reservedMap = new Map(props.reservedDefinitions.filter((entry) => entry.id).map((entry) => [entry.id, entry.usedByLabel]));

    const renderTargetSelect = (row: MappingRow) => {
        const chosenElsewhere = new Set(
            rows.filter((other) => other.rowId !== row.rowId && other.definitionId).map((other) => other.definitionId)
        );

        const options = props.definitions.filter((definition) => {
            const id = definition.legacyIDV2 ?? "";
            if (!id) {
                return false;
            }
            // Keep the currently-selected definition visible even if chosen elsewhere, so the row stays valid.
            if (id === row.definitionId) {
                return true;
            }
            // Drop definitions already chosen in another row (no duplicate targets). Reserved definitions
            // stay in the list but render disabled below.
            return !chosenElsewhere.has(id);
        });

        return (
            <select
                className={"form-select form-select-sm"}
                value={row.definitionId}
                disabled={!props.isEnhanced}
                onChange={(e) => handleTargetChange(row.rowId, e.target.value)}
                data-testid={`mapping-target-${row.rowId}`}>
                <option value={""}>{t("select_a_field", "Select a field")}</option>
                {options.map((definition) => {
                    const id = definition.legacyIDV2 as string;
                    const usedByLabel = reservedMap.get(id);
                    // A reserved definition is selectable only if it's already this row's saved value.
                    const isReserved = !!usedByLabel && id !== row.definitionId;
                    const label = isReserved
                        ? `${definition.label} (${t("mapping_field_in_use", "in use")}: ${t(usedByLabel as string)})`
                        : definition.label;
                    return (
                        <option key={id} value={id} disabled={isReserved}>
                            {label}
                        </option>
                    );
                })}
            </select>
        );
    };

    const renderSourceSelect = (row: MappingRow) => (
        <select
            className={"form-select form-select-sm"}
            value={row.sourceField}
            disabled={!props.isEnhanced}
            onChange={(e) => updateRow(row.rowId, { sourceField: e.target.value })}
            data-testid={`mapping-source-${row.rowId}`}>
            <option value={""}>{t("select_a_field", "Select a field")}</option>
            {groupedFields.map((group) => (
                <optgroup key={group.group} label={t(`mapping_field_group_${group.group}`, group.group)}>
                    {group.fields.map((field) => (
                        <option key={field.path} value={field.path}>
                            {field.label}
                        </option>
                    ))}
                </optgroup>
            ))}
        </select>
    );

    // A value input for a static value OR a fallback. STRING_LIST targets get a dropdown of their options
    // (storing the option `value`); everything else gets free text. `fieldKey` selects which row field to bind.
    const renderValueInput = (row: MappingRow, fieldKey: "staticValue" | "defaultValue", placeholder: string) => {
        const definition = findDefinition(row.definitionId);
        const value = row[fieldKey];

        if (definition && definition.dataType === STRING_LIST) {
            const options = definition.dropDownOptions ?? [];
            return (
                <select
                    className={"form-select form-select-sm"}
                    value={value}
                    disabled={!props.isEnhanced}
                    onChange={(e) => updateRow(row.rowId, { [fieldKey]: e.target.value })}
                    data-testid={`mapping-${fieldKey}-${row.rowId}`}>
                    <option value={""}>{t("select_an_option", "Select an option")}</option>
                    {options.map((option) => (
                        <option key={option.id} value={option.value}>
                            {option.value}
                        </option>
                    ))}
                </select>
            );
        }

        return (
            <OwInputWrapper
                name={`mapping-${fieldKey}-${row.rowId}`}
                value={value}
                isSmall={true}
                disabled={!props.isEnhanced}
                placeholder={placeholder}
                fn={(e: React.ChangeEvent<HTMLInputElement>) => updateRow(row.rowId, { [fieldKey]: e.target.value })}
            />
        );
    };

    return (
        <div className={"CustomFieldMappings col-12"}>
            <div className={"col-12 d-flex align-items-center"}>
                <OwLabel className={"me-2"} title={"qbo_custom_field_mappings"} isBold={true} />
            </div>
            <div>
                <WhatsThis subject={"help_text_qbo_custom_field_mappings"} />
            </div>

            {!props.isEnhanced ? (
                <div className={"col-12 mb-3"}>
                    <OwLabel className={"text-muted"} title={"qbo_enhanced_required_note"} preserveNewlines={true} />
                </div>
            ) : null}

            {rows.length === 0 ? (
                <div className={"col-12 mb-2"}>
                    <OwLabel className={"text-muted"} title={"no_custom_field_mappings"} />
                </div>
            ) : null}

            {rows.map((row) => (
                <div key={row.rowId} className={"mapping-row col-12 mb-3"}>
                    <div className={"row g-2 align-items-end"}>
                        <div className={"col-12 col-md-3"}>
                            <OwLabel className={"d-block"} title={"mapping_target_field"} />
                            {renderTargetSelect(row)}
                        </div>

                        <div className={"col-12 col-md-3"}>
                            <OwLabel className={"d-block"} title={"mapping_source_type"} />
                            <OwRadioSelect
                                name={`mapping-source-type-${row.rowId}`}
                                translateUseLabel={true}
                                value={row.sourceType}
                                options={[
                                    { value: "field", label: "mapping_source_order_field" },
                                    { value: "static", label: "mapping_source_static_value" },
                                    { value: "order", label: "mapping_source_capture_per_order" },
                                ]}
                                fn={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    updateRow(row.rowId, { sourceType: e.target.value as SourceType })
                                }
                            />
                        </div>

                        <div className={"col-12 col-md-3"}>
                            {row.sourceType === "field" ? (
                                <>
                                    <OwLabel className={"d-block"} title={"mapping_source_order_field"} />
                                    {renderSourceSelect(row)}
                                </>
                            ) : row.sourceType === "static" ? (
                                <>
                                    <OwLabel className={"d-block"} title={"mapping_source_static_value"} />
                                    {renderValueInput(row, "staticValue", t("mapping_static_value_placeholder", "Static value"))}
                                </>
                            ) : (
                                <OwLabel className={"d-block text-muted fst-italic mt-4"} title={"mapping_captured_per_order_note"} />
                            )}
                        </div>

                        <div className={"col-12 col-md-2"}>
                            {row.sourceType !== "static" ? (
                                <>
                                    <OwLabel className={"d-block"} title={"mapping_default_value"} />
                                    {renderValueInput(row, "defaultValue", t("mapping_default_value_placeholder", "Fallback (optional)"))}
                                </>
                            ) : null}
                        </div>

                        <div className={"col-12 col-md-1 text-end"}>
                            {props.isEnhanced ? (
                                <div>
                                    <OwButton
                                        className={"btn-danger btn-sm"}
                                        centerIconClass={"fas fa-trash-alt"}
                                        ariaLabel={t("remove", "Remove")}
                                        fn={() => removeRow(row.rowId)}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ))}

            {props.isEnhanced ? (
                <div className={"col-12 d-flex gap-2 mt-2"}>
                    <OwButton className={"btn-outline-primary btn-sm"} leftIconClass={"fas fa-plus"} title={"add_mapping"} fn={addRow} />
                    <OwButton
                        className={"btn-primary btn-sm"}
                        disabled={props.saving}
                        active={props.saving}
                        title={"save"}
                        fn={handleSave}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default CustomFieldMappings;
